import { apiFetch } from './api';

const apiUrl = import.meta.env.VITE_API_URL;

/** lead | msg | tarefa | fechado */
export type TipoAviso = 'lead' | 'msg' | 'tarefa' | 'fechado';

export interface Aviso {
    tipo: TipoAviso;
    /** Identifica o objeto do aviso; é o que volta em `marcarLido`. */
    chave: string;
    titulo: string;
    detalhe: string;
    quando: string;
    conversaId: number | null;
    tarefaId: number | null;
    lido: boolean;
}

export interface ResumoNotificacoes {
    avisosNaoLidos: number;
    tarefasVencidas: number;
    /** Conversas com mensagem de cliente não lida (o que o badge e a lista mostram). Quem zera é o chat, ao abrir a conversa. */
    mensagensNaoLidas: number;
    /** Soma das MENSAGENS não lidas. Serve só de gatilho do som: conversas não mudam a cada mensagem do mesmo cliente. */
    mensagensNovasTotal: number;
}

interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

/**
 * O sino. Os avisos nascem de consulta na API — não existe fila —, e o que se grava é
 * só a marca de lido. docs/designsystem/propostas/notificacoes.html §6 e §7.
 */
export const NotificacaoService = {
    async getAvisos(somenteNaoLidos = false): Promise<Aviso[]> {
        const q = somenteNaoLidos ? '?somenteNaoLidos=true' : '';
        const res = await apiFetch(`${apiUrl}/api/Notificacao/Avisos${q}`);
        const r: ApiResponse<Aviso[]> = await res.json();
        return r.sucesso && Array.isArray(r.dados) ? r.dados : [];
    },

    /** Um aviso ou o lote inteiro — "marcar todos como lidos" é o mesmo gesto. */
    async marcarLido(itens: { tipo: string; chave: string }[]): Promise<boolean> {
        if (itens.length === 0) return true;
        const res = await apiFetch(`${apiUrl}/api/Notificacao/MarcarLido`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ itens }),
        });
        const r: ApiResponse<null> = await res.json();
        return r.sucesso;
    },

    /**
     * A apresentação do Brik AI na Home (07/10/2026), vista uma vez por pessoa. A marca
     * mora na mesma tabela do "lido" do sino, com um tipo que o sino não lê — por isso
     * vale em qualquer navegador. A API devolve se já foi vista em `Assistente/Abertura`.
     */
    async marcarApresentacaoVista(): Promise<boolean> {
        return NotificacaoService.marcarLido([{ tipo: 'apresentacao', chave: 'brik-ai' }]);
    },

    /**
     * A marca que quem criou a conta deixa no guia de boas-vindas (/bem-vindo, 28/10/2026):
     * "concluido" ou "pulado-N" (N = o passo em que saiu). A API lê no `Auth/Me` para não
     * mandar a pessoa de volta ao guia, e Sistema › Empresas mostra na aba Usuários.
     */
    async marcarBoasVindas(chave: 'concluido' | `pulado-${number}`): Promise<boolean> {
        return NotificacaoService.marcarLido([{ tipo: 'boas-vindas', chave }]);
    },

    /**
     * As novidades que a pessoa já marcou como vistas ("Entendi" no modal, 08/10/2026),
     * na mesma tabela do "lido". `null` quando não deu para saber — inclusive com a API
     * de antes do `GET Lidos` (404): aí vale só a marca do navegador.
     */
    async novidadesVistas(): Promise<string[] | null> {
        try {
            const res = await apiFetch(`${apiUrl}/api/Notificacao/Lidos?tipo=novidade`);
            if (!res.ok) return null;
            const r: ApiResponse<string[]> = await res.json();
            return r.sucesso && Array.isArray(r.dados) ? r.dados : null;
        } catch {
            return null;
        }
    },

    async marcarNovidadeVista(chave: string): Promise<boolean> {
        return NotificacaoService.marcarLido([{ tipo: 'novidade', chave }]);
    },

    /**
     * Os três números da sidebar numa chamada só. A virada do dia vai daqui porque
     * é o navegador que sabe o fuso de quem está olhando.
     */
    async resumo(): Promise<ResumoNotificacoes> {
        const virada = new Date();
        virada.setHours(0, 0, 0, 0);
        const res = await apiFetch(`${apiUrl}/api/Notificacao/Resumo?viradaDoDia=${encodeURIComponent(virada.toISOString())}`);
        const r: ApiResponse<ResumoNotificacoes> = await res.json();
        return r.sucesso && r.dados ? r.dados : { avisosNaoLidos: 0, tarefasVencidas: 0, mensagensNaoLidas: 0, mensagensNovasTotal: 0 };
    },
};
